import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../api";
import {
    fetchSessionStatement,
    emailSessionStatement,
    exportSessionStatementUrl,
} from "../../api/studentReports";
import { getActiveRole } from "../../utils/auth";
import { usePortalStudentId } from "../../hooks/usePortalStudentId";
import ParentPortalHeader from "../../components/parent/ParentPortalHeader";
import DatePicker from "../../components/common/DatePicker";
import UserLink from "../../components/common/UserLink";
import ReportExportBar from "../../components/reports/ReportExportBar";
import ReportGenerateButton from "../../components/reports/ReportGenerateButton";
import ExcelDownloadButton from "../../components/common/ExcelDownloadButton";
import { exportMultiSheet } from "../../utils/excelExport";
import { formatCurrency } from "../../utils/currency";
import PageHeaderSection from "../../components/layout/PageHeaderSection";
import PaginationFooter from "../../components/common/PaginationFooter";
import SortableTableHeader from "../../components/common/SortableTableHeader";
import useSortedClientPagination from "../../hooks/useSortedClientPagination";
import {
    sessionStatementWalletSortColumns,
    sessionStatementSessionSortColumns,
} from "../../config/tableSortColumns";
import SessionDateTimeDisplay from "../../components/common/SessionDateTimeDisplay";
import { studentDisplayName } from '../../utils/displayNames';
import { apiErrorMessage } from '../../utils/apiError';
import {
    regionalDateDaysAgo,
    todayRegionalDate,
    toRegionalDateFromISO,
    toISODateFromRegional,
    todayIso,
} from "../../utils/date";

const parseStudentNameAndId = (studentString) => {
    const match = studentString.match(/(.*) \(#(\d+)\)/);
    if (match) {
        return {
            name: match[1].trim(),
            id: match[2]
        };
    }
    return { name: studentString, id: null };
};

const minusDaysIso = (days) => {
    const iso = regionalDateDaysAgo(days);
    const parsed = toISODateFromRegional(iso);
    return parsed || todayIso();
};

const isoToRegional = toRegionalDateFromISO;

const regionalToIso = (str) => {
    const iso = toISODateFromRegional(str);
    return iso || undefined;
};

const SessionStatement = ({ embeddedInHub = false }) => {
    const { t } = useTranslation();
    const role = getActiveRole();

    const isStudent = role === "STUDENT";
    const isParent = role === "PARENT";
    const childStudentId = usePortalStudentId();
    const isAdmin = role === "ADMIN";
    const isManager = role === "MANAGER";
    const isCoach = role === "COACH";

    const [from, setFrom] = useState(isoToRegional(minusDaysIso(30)));
    const [to, setTo] = useState(isoToRegional(todayIso()));
    const [studentId, setStudentId] = useState("");
    const [students, setStudents] = useState([]);

    const [data, setData] = useState(null);

    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [studentsLoadError, setStudentsLoadError] = useState("");

    useEffect(() => {
        if (isStudent || isParent) return;

        const loadStudents = async () => {
            setStudentsLoadError("");
            try {
                let res;
                if (isAdmin) {
                    res = await API.get("/admin/users", { params: { role: "STUDENT" } });
                } else if (isManager) {
                    res = await API.get("/manager/students");
                } else if (isCoach) {
                    res = await API.get("/coach/students");
                }
                setStudents(res?.data || []);
            } catch (e) {
                console.error("Failed to load students", e);
                setStudents([]);
                setStudentsLoadError(t('common.optionsLoadFailed'));
            }
        };

        loadStudents();
    }, [isStudent, isAdmin, isManager, isCoach, t]);

    const load = async () => {
        setLoading(true);
        setErr("");

        try {
            let result;
            const fromIso = regionalToIso(from);
            const toIso = regionalToIso(to);

            if (isStudent || isParent) {
                result = await fetchSessionStatement({
                    from: fromIso,
                    to: toIso,
                    childStudentId: isParent ? childStudentId : null,
                });
            } else {
                if (!studentId) {
                    setErr(t('pages.sessionStatement.selectStudentError'));
                    setLoading(false);
                    return;
                }

                const res = await API.get("/reports/session-statement", {
                    params: {
                        studentId,
                        fromDate: fromIso,
                        toDate: toIso
                    }
                });
                result = res.data;
            }

            setData(result);
        } catch (e) {
            setErr(apiErrorMessage(e, t('pages.sessionStatement.loadFailed')));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isStudent || (isParent && childStudentId)) load();
        // eslint-disable-next-line
    }, [isStudent, isParent, childStudentId]);

    const exportPdf = async () => {
        try {
            const res = await API.get(
                (isStudent || isParent)
                    ? exportSessionStatementUrl({
                        from: regionalToIso(from),
                        to: regionalToIso(to),
                        childStudentId: isParent ? childStudentId : null,
                    })
                    : "/reports/session-statement/export",
                {
                params: (isStudent || isParent) ? undefined : {
                    fromDate: regionalToIso(from),
                    toDate: regionalToIso(to),
                    studentId,
                    format: "pdf"
                },
                responseType: "blob"
            });

            const blob = new Blob([res.data], { type: "application/pdf" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "session-statement.pdf";
            a.click();
            URL.revokeObjectURL(a.href);
        } catch {
            alert(t('pages.sessionStatement.exportPdfFailed'));
        }
    };

    // Filter out discount credits from wallet transactions
    const nonDiscountCredits = (data?.creditRows || []).filter(r => {
        const desc = (r.description || '').toLowerCase();
        return !desc.includes('discount');
    });
    const totalWalletCredits = nonDiscountCredits.reduce((sum, r) => sum + (r.amount || 0), 0);
    const presentSessions = (data?.rows || []).filter(r => r.attendance === "P").length;
    const absentSessions = (data?.rows || []).filter(r => r.attendance === "A").length;

    const reportKey = data ? `${data.student}-${data.from}-${data.to}` : null;
    const walletPagination = useSortedClientPagination(nonDiscountCredits, {
        columns: sessionStatementWalletSortColumns,
        resetKey: reportKey,
        defaultSortKey: 'date',
        defaultSortDir: 'desc',
    });
    const sessionPagination = useSortedClientPagination(data?.rows ?? [], {
        columns: sessionStatementSessionSortColumns,
        resetKey: reportKey,
        defaultSortKey: 'dateTime',
        defaultSortDir: 'desc',
    });

    return (
        <div className={embeddedInHub ? "space-y-6" : "list-page"}>
            <ParentPortalHeader />
            {!embeddedInHub && (
                <PageHeaderSection
                    eyebrow={t('pages.sessionStatement.eyebrow')}
                    title={t('pages.sessionStatement.title')}
                    subtitle={t('pages.sessionStatement.subtitle')}
                />
            )}
            {embeddedInHub && (
                <p className="text-sm text-gray-600">{t('pages.sessionStatement.embeddedSubtitle')}</p>
            )}

            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    {t('pages.sessionStatement.filters')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {!isStudent && !isParent && (
                        <div className="md:col-span-2">
                            <label className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                🎯 {t('pages.sessionStatement.selectStudent')}
                            </label>
                            <select
                                value={studentId}
                                onChange={(e) => setStudentId(e.target.value)}
                                className="border border-gray-300 p-2.5 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm h-[42px]"
                            >
                                <option value="">{t('pages.sessionStatement.chooseStudent')}</option>
                                {students.map((s) => {
                                    const nameParts = [s.firstName, s.middleName, s.lastName].filter(Boolean);
                                    const fullName = nameParts.length > 0 ? nameParts.join(" ") : s.username || studentDisplayName(null, s.id, t);
                                    return (
                                        <option key={s.id} value={s.id}>
                                            {fullName} (#{s.id})
                                        </option>
                                    );
                                })}
                            </select>
                            {studentsLoadError && (
                                <p className="mt-2 text-sm text-red-700" role="alert">{studentsLoadError}</p>
                            )}
                        </div>
                    )}

                    <div>
                        <label className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                            📅 {t('pages.sessionStatement.fromDate')}
                        </label>
                        <DatePicker
                            value={from}
                            onChange={setFrom}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                            📅 {t('pages.sessionStatement.toDate')}
                        </label>
                        <DatePicker
                            value={to}
                            onChange={setTo}
                            className="w-full"
                        />
                    </div>

                    <div className="flex items-end md:col-span-2">
                        <ReportGenerateButton
                            onClick={load}
                            loading={loading}
                            label={t('pages.sessionStatement.generate')}
                            loadingLabel={t('pages.sessionStatement.generating')}
                            className="w-full"
                        />
                    </div>
                </div>
            </div>

            {err && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    {err}
                </div>
            )}

            {/* Results Section */}
            {!loading && data && (
                <>
                    {/* Student Info Banner */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-md p-6 text-white">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <div className="text-sm opacity-90 mb-1">{t('pages.sessionStatement.student')}</div>
                                <div className="text-2xl font-bold">
                                    <UserLink
                                        id={parseStudentNameAndId(data.student).id}
                                        name={parseStudentNameAndId(data.student).name}
                                        className="text-white hover:text-blue-100"
                                    />
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm opacity-90 mb-1">{t('pages.sessionStatement.statementPeriod')}</div>
                                <div className="text-xl font-semibold">{data.from} - {data.to}</div>
                            </div>
                        </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md p-6 text-white">
                            <div className="text-sm opacity-90 mb-1">{t('pages.sessionStatement.openingBalance')}</div>
                            <div className="text-3xl font-bold">{formatCurrency(data.openingBalance)}</div>
                            <div className="text-xs opacity-75 mt-2">{t('pages.sessionStatement.availableAtStart')}</div>
                        </div>

                        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md p-6 text-white">
                            <div className="text-sm opacity-90 mb-1">{t('pages.sessionStatement.walletCredits')}</div>
                            <div className="text-3xl font-bold">{formatCurrency(totalWalletCredits)}</div>
                            <div className="text-xs opacity-75 mt-2">{t('pages.sessionStatement.transactionsCount', { count: nonDiscountCredits.length })}</div>
                        </div>

                        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md p-6 text-white">
                            <div className="text-sm opacity-90 mb-1">{t('pages.sessionStatement.sessionCharges')}</div>
                            <div className="text-3xl font-bold">{formatCurrency(data.totalCharges)}</div>
                            <div className="text-xs opacity-75 mt-2">{t('pages.sessionStatement.sessionsPresentAbsent', { present: presentSessions, absent: absentSessions })}</div>
                        </div>

                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md p-6 text-white">
                            <div className="text-sm opacity-90 mb-1">{t('pages.sessionStatement.closingBalance')}</div>
                            <div className="text-3xl font-bold">{formatCurrency(data.closingBalance)}</div>
                            <div className="text-xs opacity-75 mt-2">{t('pages.sessionStatement.currentBalance')}</div>
                        </div>
                    </div>

                    {/* Wallet Transactions */}
                    {nonDiscountCredits.length > 0 && (
                        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                            <div className="px-6 py-4 bg-gray-50 border-b">
                                <h3 className="text-lg font-semibold text-gray-800">{t('pages.sessionStatement.walletTransactions')}</h3>
                                <p className="text-sm text-gray-600 mt-1">{t('pages.sessionStatement.walletTransactionsHint')}</p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <SortableTableHeader label={t('table.date')} columnKey="date" sortKey={walletPagination.sortKey} sortDir={walletPagination.sortDir} onSort={walletPagination.toggleSort} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" align="center" />
                                            <SortableTableHeader label={t('table.description')} columnKey="description" sortKey={walletPagination.sortKey} sortDir={walletPagination.sortDir} onSort={walletPagination.toggleSort} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" align="center" />
                                            <SortableTableHeader label={t('table.amount')} columnKey="amount" sortKey={walletPagination.sortKey} sortDir={walletPagination.sortDir} onSort={walletPagination.toggleSort} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" align="center" />
                                            <SortableTableHeader label={t('table.balanceAfter')} columnKey="balanceAfter" sortKey={walletPagination.sortKey} sortDir={walletPagination.sortDir} onSort={walletPagination.toggleSort} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" align="center" />
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {walletPagination.pageItems.map((r, i) => (
                                            <tr key={i} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.date}</td>
                                                <td className="px-6 py-4 text-sm text-gray-700">
                                                    <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                                                        {r.description}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center font-medium text-green-600">
                                                    +{formatCurrency(r.amount || 0)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                                                    {formatCurrency(r.balanceAfter || 0)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {walletPagination.total > 0 && (
                                <PaginationFooter
                                    page={walletPagination.page}
                                    size={walletPagination.size}
                                    total={walletPagination.total}
                                    onPageChange={walletPagination.setPage}
                                    onSizeChange={walletPagination.setSize}
                                />
                            )}
                        </div>
                    )}

                    {/* Session History */}
                    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                        <div className="px-6 py-4 bg-gray-50 border-b flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">{t('pages.sessionStatement.sessionHistory')}</h3>
                                <p className="text-sm text-gray-600 mt-1">{t('pages.sessionStatement.sessionHistoryHint')}</p>
                            </div>
                            <ExcelDownloadButton
                                disabled={!data}
                                label={t('common.downloadExcel')}
                                onExport={() => {
                                    exportMultiSheet([
                                        {
                                            name: t('pages.sessionStatement.walletTransactions'),
                                            columns: [
                                                { header: t('table.date'), key: 'date', width: 14 },
                                                { header: t('table.description'), key: 'description', width: 40 },
                                                { header: t('table.amount'), key: 'amount', render: (v) => formatCurrency(v || 0), width: 12 },
                                                { header: t('table.balanceAfter'), key: 'balanceAfter', render: (v) => formatCurrency(v || 0), width: 14 },
                                            ],
                                            rows: (data?.creditRows || []).filter(r => !r.description?.includes('discount')),
                                        },
                                        {
                                            name: t('pages.sessionStatement.sessionHistory'),
                                            columns: [
                                                { header: t('table.dateTime'), key: 'dateTime', width: 20 },
                                                { header: t('table.batch'), key: 'batch', width: 20 },
                                                { header: t('table.topics'), key: 'sessionTopics', width: 30 },
                                                { header: t('common.status'), key: 'attendance', render: (v) => v === 'P' ? t('pages.sessionStatement.present') : v === 'A' ? t('pages.sessionStatement.absent') : v || '—', width: 10 },
                                                { header: t('table.balanceBefore'), key: 'balanceBefore', render: (v) => formatCurrency(v || 0), width: 14 },
                                                { header: t('table.sessionFee'), key: 'charges', render: (v) => formatCurrency(v || 0), width: 12 },
                                                { header: t('table.balanceAfter'), key: 'balanceAfter', render: (v) => formatCurrency(v || 0), width: 14 },
                                            ],
                                            rows: data?.rows || [],
                                        },
                                    ], 'session-statement');
                                }}
                            />
                        </div>
                        <div>
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <SortableTableHeader label={t('table.dateTime')} columnKey="dateTime" sortKey={sessionPagination.sortKey} sortDir={sessionPagination.sortDir} onSort={sessionPagination.toggleSort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-40" align="center" />
                                        <SortableTableHeader label={t('table.batch')} columnKey="batch" sortKey={sessionPagination.sortKey} sortDir={sessionPagination.sortDir} onSort={sessionPagination.toggleSort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-48" align="center" />
                                        <SortableTableHeader label={t('table.topics')} columnKey="topics" sortKey={sessionPagination.sortKey} sortDir={sessionPagination.sortDir} onSort={sessionPagination.toggleSort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" align="center" />
                                        <SortableTableHeader label={t('common.status')} columnKey="status" sortKey={sessionPagination.sortKey} sortDir={sessionPagination.sortDir} onSort={sessionPagination.toggleSort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24" align="center" />
                                        <SortableTableHeader label={t('table.balanceBefore')} columnKey="balanceBefore" sortKey={sessionPagination.sortKey} sortDir={sessionPagination.sortDir} onSort={sessionPagination.toggleSort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32" align="center" />
                                        <SortableTableHeader label={t('table.sessionFee')} columnKey="charges" sortKey={sessionPagination.sortKey} sortDir={sessionPagination.sortDir} onSort={sessionPagination.toggleSort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32" align="center" />
                                        <SortableTableHeader label={t('table.balanceAfter')} columnKey="balanceAfter" sortKey={sessionPagination.sortKey} sortDir={sessionPagination.sortDir} onSort={sessionPagination.toggleSort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32" align="center" />
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {data.rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                                                {t('pages.sessionStatement.noSessionsPeriod')}
                                            </td>
                                        </tr>
                                    ) : sessionPagination.pageItems.map((r, i) => (
                                            <tr key={i} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-3 align-top text-center">
                                                    <SessionDateTimeDisplay dateTime={r.dateTime} />
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700 align-top">{r.batch}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600 align-top">{r.sessionTopics}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center align-top">
                                                    <span className={`px-3 py-1 text-xs font-bold rounded-full ${r.attendance === "P"
                                                        ? "bg-green-100 text-green-800"
                                                        : r.attendance === "A"
                                                            ? "bg-red-100 text-red-800"
                                                            : "bg-gray-100 text-gray-600"
                                                        }`}>
                                                        {r.attendance === "P" ? t('pages.sessionStatement.present') : r.attendance === "A" ? t('pages.sessionStatement.absent') : r.attendance}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900 align-top">
                                                    {formatCurrency(r.balanceBefore)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-center align-top">
                                                    {Number(r.charges) === 0 ? (
                                                        <div className="text-gray-600">{formatCurrency(0)}</div>
                                                    ) : (
                                                        <>
                                                            <div className="font-medium text-red-600">
                                                                {formatCurrency(r.charges)}
                                                            </div>
                                                            {Number(r.discount) > 0 && (
                                                                <div className="text-xs text-green-600 mt-0.5">
                                                                    {t('pages.sessionStatement.discount', { amount: formatCurrency(r.discount) })}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-semibold text-gray-900 align-top">
                                                    {formatCurrency(r.balanceAfter)}
                                                </td>
                                            </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {sessionPagination.total > 0 && (
                            <PaginationFooter
                                page={sessionPagination.page}
                                size={sessionPagination.size}
                                total={sessionPagination.total}
                                onPageChange={sessionPagination.setPage}
                                onSizeChange={sessionPagination.setSize}
                            />
                        )}
                    </div>

                    <ReportExportBar
                        disabled={!data}
                        onExportPdf={exportPdf}
                        onSendEmail={isStudent ? async (emailAddr) => {
                            await emailSessionStatement({
                                from: regionalToIso(from),
                                to: regionalToIso(to),
                                toEmail: emailAddr || undefined,
                            });
                        } : undefined}
                        emailLabel={t('pages.sessionStatement.emailLabel')}
                    />
                </>
            )}
        </div>
    );
};

export default SessionStatement;
